<!-- Join Gold Modal — Vue twin of
     resources/views/members3/partials/join_gold_modal.blade.php. Same product,
     two hand-synced implementations: no shared component, no import between
     them. CHANGE BOTH, in the same commit.

     See the `blade-vue-twins` skill for the full invariant list. The short
     version: event names must match exactly (the ancms funnels join on them),
     and appendGoldAttribution() must stay identical to the Blade twin's
     jgmAppendGoldAttribution() — psc4 always overwritten, gated on `source`
     rather than creativeId. That last rule is what this file got wrong: the
     Blade side was fixed in 075f2aa4c5 (2026-08-17) and this side was missed
     until e5bc9885a2 two days later, during which every GOLD purchase from a
     Vue surface reported this modal as its own source and fell out of
     interstitial_ad_gold_conversions.pipe.

     Legitimate differences (don't "fix" them): this side styles with inline
     Tailwind rather than the Blade twin's jgm- CSS, reads its data from Inertia
     shared props instead of calling PricingHelper/GoldController inline, opens
     via the isOpen prop instead of window.openJoinGoldModal(), and carries an
     `audition` branch with no Blade equivalent. -->
<template>
    <Teleport to="body">
        <Transition name="modal">
            <div v-if="isOpen"
                 @click.self="dismiss()"
                 class="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center p-4 z-[51]"
            >
                <!-- NEX-9344 redesign variant. Reuses handleJoin/selectOption/appendGoldAttribution
                     untouched — only the presentation differs. All copy comes from the shared
                     goldModal.copy prop (config/gold-modal.php), the same source the Blade twin
                     reads, so the two can't drift. -->
                <div v-if="isRedesign" class="jgm-scroll jgmx-modal">
                    <button @click="dismiss()" class="jgmx-close" aria-label="Close">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>

                    <div class="jgmx-emblem" aria-hidden="true">
                        <span class="jgmx-emblem-glow"></span>
                        <span class="jgmx-emblem-ring">
                            <svg viewBox="0 0 24 24">
                                <defs>
                                    <linearGradient id="jgmxStarGradVue" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0" stop-color="#F8D57F"/>
                                        <stop offset=".5" stop-color="#E3B441"/>
                                        <stop offset="1" stop-color="#A9741C"/>
                                    </linearGradient>
                                </defs>
                                <path fill="url(#jgmxStarGradVue)" d="M12 1.6l3.09 6.26 6.91 1-5 4.87 1.18 6.88L12 17.27 5.82 20.6 7 13.73l-5-4.87 6.91-1z"/>
                            </svg>
                        </span>
                    </div>

                    <p class="jgmx-eyebrow">{{ redesignShared.eyebrow }}</p>
                    <h2 class="jgmx-title">
                        {{ redesignCopy.headline_lead }}<br>
                        <span class="jgmx-title-gold">{{ redesignCopy.headline_accent }}</span>
                    </h2>
                    <!-- NA-authored config copy, not member input; the <b> emphasis is part of the
                         approved line. Same treatment as the Blade twin. -->
                    <p class="jgmx-sub" v-html="redesignCopy.sub"></p>

                    <template v-if="lifetimeGoldOption">
                        <div class="jgmx-pricebox">
                            <div class="jgmx-onetime">{{ redesignCopy.price_label }}</div>
                            <div class="jgmx-price">
                                ${{ lifetimePriceParts.dollars }}<sup>.{{ lifetimePriceParts.cents }}</sup>
                            </div>
                            <p class="jgmx-price-note" v-html="redesignCopy.price_note"></p>
                        </div>

                        <button type="button" class="jgmx-cta" @click="handleJoin('primary')">
                            {{ redesignCopy.cta_label }}
                            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" stroke="#1a1305" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
                        </button>
                    </template>

                    <template v-else>
                        <div class="jgmx-planbox">
                            <div class="jgmx-planbox-label"><span class="jgmx-onetime">{{ redesignCopy.picker_label }}</span></div>
                            <div class="jgmx-plans">
                                <button
                                    v-for="option in joinOptions"
                                    :key="option.name"
                                    type="button"
                                    class="jgmx-plan"
                                    :class="{ 'jgmx-plan--active': selectedOption === option.name }"
                                    @click="selectOption(option.name, option.type)"
                                >
                                    <span v-if="redesignPlanCopy(option.name).badge" class="jgmx-plan-badge">
                                        {{ redesignPlanCopy(option.name).badge }}
                                    </span>
                                    <span class="jgmx-plan-top">
                                        <span>
                                            <span class="jgmx-plan-amt">
                                                <span class="jgmx-plan-day">{{ option.dailyDisplayPrice }}</span>
                                                <span class="jgmx-plan-per">/day</span>
                                            </span>
                                            <span class="jgmx-plan-name">{{ option.name }}</span>
                                        </span>
                                        <span class="jgmx-plan-tick">
                                            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12.5l4.2 4.2L19 6.6" stroke="#1a1305" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
                                        </span>
                                    </span>
                                    <span class="jgmx-plan-inc">
                                        <span v-for="bullet in redesignPlanCopy(option.name).bullets" :key="bullet" class="jgmx-plan-inc-item">
                                            <span class="jgmx-plan-ck">
                                                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12.5l4.2 4.2L19 6.6" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
                                            </span>
                                            <span>{{ bullet }}</span>
                                        </span>
                                    </span>
                                </button>
                            </div>
                            <p class="jgmx-billing">
                                Annual billed at {{ yearlyTotalPrice }}/year
                                <span class="jgmx-billing-sep">&middot;</span>
                                Monthly billed at {{ monthlyTotalPrice }}/month
                            </p>
                        </div>

                        <button type="button" class="jgmx-cta" @click="handleJoin('primary')">
                            {{ redesignCopy.cta_label }}
                            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" stroke="#1a1305" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
                        </button>
                    </template>

                    <p class="jgmx-reassure">
                        <template v-for="(line, index) in redesignCopy.reassure" :key="line">
                            <span v-if="index > 0" class="jgmx-reassure-sep">&middot;</span>{{ line }}
                        </template>
                    </p>

                    <div class="jgmx-benefits">
                        <div v-for="benefit in redesignShared.benefits" :key="benefit.label" class="jgmx-benefit">
                            <span class="jgmx-benefit-ck">
                                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12.5l4.2 4.2L19 6.6" stroke="#1a1305" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            </span>
                            <b>{{ benefit.label }}</b>
                            <span v-if="benefit.detail" class="jgmx-benefit-det">{{ benefit.detail }}</span>
                        </div>
                    </div>

                    <button type="button" class="jgmx-toggle" :aria-expanded="comparisonOpen ? 'true' : 'false'"
                            aria-controls="jgmxCompareVue" @click="toggleComparison()">
                        <span>{{ comparisonOpen ? redesignShared.comparison.toggle_hide : redesignShared.comparison.toggle_show }}</span>
                        <span class="jgmx-chev" :class="{ 'jgmx-chev--open': comparisonOpen }" aria-hidden="true">&#9662;</span>
                    </button>

                    <div id="jgmxCompareVue" class="jgmx-compare" :class="{ 'jgmx-compare--open': comparisonOpen }">
                        <div class="jgmx-compare-inner">
                            <table class="jgmx-cmp">
                                <thead>
                                    <tr>
                                        <th class="jgmx-cmp-trial">{{ redesignShared.comparison.trial_note }}</th>
                                        <th class="jgmx-cmp-na">{{ redesignShared.comparison.columns[0] }}</th>
                                        <th class="jgmx-cmp-gold">{{ redesignShared.comparison.columns[1] }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in redesignShared.comparison.rows" :key="row[0]">
                                        <td class="jgmx-cmp-feat">{{ row[0] }}</td>
                                        <!-- '_yes'/'_no' are drawn as marks so the copy source never
                                             carries glyphs the two twins could diverge on. -->
                                        <td v-for="(col, idx) in [1, 2]" :key="idx"
                                            :class="col === 1 ? 'jgmx-cmp-navalue' : 'jgmx-cmp-goldvalue'">
                                            <span v-if="row[col] === '_yes'" class="jgmx-cmp-yes" aria-label="Included">&#10003;</span>
                                            <span v-else-if="row[col] === '_no'" class="jgmx-cmp-no" aria-label="Not included">&mdash;</span>
                                            <template v-else>{{ row[col] }}</template>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <ul class="jgmx-sitelist">
                                <li v-for="site in bonusSites" :key="site.name">{{ site.name }}</li>
                            </ul>
                            <p class="jgmx-cmp-note">{{ redesignShared.comparison.note }}</p>

                            <!-- Second CTA. Same handler as the primary; cta_position is what lets
                                 reporting tell the two apart (AC #8). -->
                            <button type="button" class="jgmx-cta jgmx-cta--secondary" @click="handleJoin('comparison')">
                                {{ redesignCopy.cta_label }}
                                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" stroke="#1a1305" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            </button>
                        </div>
                    </div>

                    <p class="jgmx-disclaimer">{{ redesignCopy.disclaimer }}</p>
                    <button type="button" class="jgmx-later" @click="dismiss()">Maybe later</button>
                </div>

                <div v-else class="jgm-scroll max-w-lg w-full bg-white rounded-3xl shadow-xl overflow-hidden relative max-h-[90vh] overflow-y-auto">

                    <!-- Close Button -->
                    <button
                        @click="dismiss()"
                        class="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/20 hover:bg-black/30 transition-colors"
                    >
                        <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>

                    <!-- Header + pricing/option section share /gold's own dark hero
                         backdrop (naGoldNew.blade.php's .g-pricing: a heavily
                         grayscaled + darkened photo, plus a soft gold glow) rather
                         than this modal's plain white background, so the member
                         recognizes the same "Experience Gold Today" moment
                         wherever it's promoted from. See .jgm-hero-* below. -->
                    <div class="jgm-hero">
                        <div class="jgm-hero-bg" aria-hidden="true"></div>
                        <div class="jgm-hero-overlay" aria-hidden="true"></div>
                        <div class="jgm-hero-glow" aria-hidden="true"></div>

                        <div class="jgm-hero-content">
                        <!-- Header Section -->
                        <div class="px-8 pt-8 pb-6 text-center">
                            <!-- Same heading + styling as /gold's own pricing heading
                                 (naGoldNew.blade.php's #g-pricing-heading /
                                 .g-pricing__heading-gold): "Gold" rendered in the same
                                 gold-gradient text-clip, "Experience"/"Today" in white. -->
                            <h2 class="jgm-hero-heading">
                                Experience <span class="jgm-hero-heading-gold">Gold</span> Today
                            </h2>
                            <p class="text-sm mt-2" style="color: rgba(255, 255, 255, 0.7);">Double your Star Rewards</p>
                        </div>

                        <!-- Lifetime GOLD (PricingHelper::getLifetimeGoldOption()) is what this
                             modal promotes — a single one-time payment, no recurring plan to
                             pick between. Falls back to the Annual/Monthly picker below only
                             when Lifetime isn't a real option for this member (see
                             lifetimeGoldOption's doc). -->
                        <template v-if="lifetimeGoldOption">
                        <!-- Mirrors /gold's own Plus Gold pricing card exactly
                             (naGoldNew.blade.php's .g-plan--annual.g-plan--solo,
                             the "pricing_section" placement) rather than this
                             modal's own light card style, so the member sees the
                             identical offer whichever surface promoted it: same
                             translucent gold-glass card (rgba(212,175,55,0.07),
                             not a solid dark fill), floating crown badge, big white
                             price, gold gradient CTA, and the same billing note
                             copy — crown_label/period/billing_note all come from
                             PricingHelper::getLifetimeGoldOption(), including its
                             recurring-vs-one-time branch, so this can't drift
                             from /gold's own copy. No perks bullet list here,
                             matching that card's actual (sparser) markup. -->
                        <div class="px-6 pt-2 pb-3 space-y-3">
                            <div
                                class="relative rounded-2xl px-6 pt-9 pb-6 text-center"
                                style="background: rgba(212, 175, 55, 0.07); border: 1px solid rgba(212, 175, 55, 0.4); box-shadow: 0 0 60px rgba(212, 175, 55, 0.08), 0 24px 60px rgba(0, 0, 0, 0.5);"
                            >
                                <span
                                    class="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-3.5 py-1 text-[10px] font-extrabold uppercase text-black"
                                    style="letter-spacing: 2px; background: linear-gradient(90deg, #b8860b, #f0d078, #c9a84c); z-index: 5;"
                                >
                                    {{ lifetimeGoldOption.data.crown_label }}
                                </span>

                                <div class="flex items-baseline justify-center gap-0.5 mb-1">
                                    <span class="text-lg font-bold" style="color: rgba(255, 255, 255, 0.7);">$</span>
                                    <span class="text-5xl font-extrabold text-white" style="letter-spacing: -2px;">{{ lifetimePriceParts.dollars }}</span>
                                    <span class="text-xl font-bold" style="color: rgba(255, 255, 255, 0.7);">.{{ lifetimePriceParts.cents }}</span>
                                </div>
                                <p class="text-xs uppercase mb-5" style="letter-spacing: 1.2px; color: rgba(255, 255, 255, 0.45);">{{ lifetimeGoldOption.data.period }}</p>

                                <button
                                    @click="handleJoin"
                                    class="block w-full text-center py-4 px-6 rounded-[10px] text-sm font-extrabold uppercase text-black mt-3 transition"
                                    style="letter-spacing: 1.5px; background: linear-gradient(135deg, #b8860b 0%, #f0d078 50%, #c9a84c 100%); box-shadow: 0 10px 32px rgba(212, 175, 55, 0.4);"
                                >
                                    Upgrade to Gold
                                </button>
                                <p class="text-[10px] mt-3" style="color: rgba(255, 255, 255, 0.4);">
                                    {{ lifetimeGoldOption.data.billing_note }}
                                </p>
                            </div>
                        </div>
                    </template>

                        <template v-else>
                        <!-- Join Options -->
                        <div class="px-6 pt-2 pb-3 space-y-3">
                            <button
                                v-for="option in joinOptions"
                                :key="option.name"
                                @click="selectOption(option.name, option.type)"
                                :class="[
                                    'relative w-full p-5 rounded-2xl border-2 transition-all text-left hover:shadow-2xl active:scale-[0.99]',
                                    selectedOption === option.name
                                        ? 'border-amber-500 bg-gray-900 ring-4 ring-amber-500/20'
                                        : 'border-gray-200 bg-white opacity-70 hover:opacity-100'
                                ]"
                            >
                                <div v-if="option.name === 'Annual'" class="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-1 bg-amber-500 text-black text-xs font-bold rounded-full shadow-lg">
                                    BEST VALUE • SAVE 38%
                                </div>

                                <div class="flex items-center justify-between mt-1">
                                    <div class="flex-1">
                                        <div class="flex items-baseline gap-1 mb-2">
                                            <span :class="['text-5xl font-bold', selectedOption === option.name ? 'text-white' : 'text-gray-900']">
                                                {{ option.dailyDisplayPrice }}
                                            </span>
                                            <span :class="['text-lg', selectedOption === option.name ? 'text-gray-300' : 'text-gray-600']">
                                                /day
                                            </span>
                                        </div>
                                        <div :class="['text-xs font-semibold uppercase tracking-wide mb-2', selectedOption === option.name ? 'text-gray-400' : 'text-gray-500']">
                                            {{ option.name }}
                                        </div>

                                        <div class="space-y-1.5">
                                            <div v-for="content in option.includedContent" :key="content" class="flex items-center gap-1.5">
                                                <svg :class="['w-3.5 h-3.5 flex-shrink-0', selectedOption === option.name ? 'text-amber-500' : 'text-gray-400']" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                                                </svg>
                                                <span :class="['text-xs', selectedOption === option.name ? 'text-white' : 'text-gray-900']">
                                                    {{ content }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </button>

                            <!-- Billing Details -->
                            <div class="text-center pt-2 pb-1">
                                <p class="text-xs text-white/60">
                                    Annual plan billed at {{ yearlyTotalPrice }}/year • Monthly plan billed at {{ monthlyTotalPrice }}/month
                                </p>
                            </div>

                            <!-- CTA Button -->
                            <button
                                @click="handleJoin"
                                class="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg rounded-xl transition-all shadow-lg hover:shadow-xl active:scale-[0.99] mt-2"
                            >
                                Upgrade to GOLD
                            </button>
                        </div>
                        </template>
                        </div>
                    </div>

                    <!-- Bonus Sites Section -->
                    <div class="px-6 pt-6 pb-6">
                        <div class="mb-3">
                            <div class="text-xs font-semibold text-gray-900 mb-1">Included with every plan</div>
                            <!-- bonusSites.length, not the "11 total sites" figure used
                                 elsewhere (config('gold-sites.total_sites')) — this grid
                                 only shows the bonus sites, not Naughty America itself,
                                 so the count here must match what's actually rendered. -->
                            <div class="text-xs text-gray-500">Unlimited streaming on {{ bonusSites.length }} bonus sites</div>
                        </div>

                        <div class="grid grid-cols-2 gap-2 mb-4">
                            <div
                                v-for="site in bonusSites"
                                :key="site.name"
                                class="flex items-center justify-center p-3 bg-gray-50 rounded-lg h-14"
                            >
                                <img :src="site.logo" :alt="site.name" class="max-h-6 max-w-[85%] object-contain">
                            </div>
                        </div>

                        <!-- Trust Indicators -->
                        <div class="flex items-center justify-center gap-3 text-xs text-gray-500 pt-4">
                            <span class="flex items-center gap-1">
                                <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/>
                                </svg>
                                Secure Payment
                            </span>
                            <span class="text-gray-300">•</span>
                            <span>Instant Access</span>
                            <span class="text-gray-300">•</span>
                            <span>No Commitment</span>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import {ref, defineEmits, computed, watch} from 'vue';
import {usePage} from "@inertiajs/vue3";
import {trackAuditionAnalyticsEvent} from "@/Helpers/auditions";
import {trackEvent} from "@/Helpers/analytics";

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true
    },
    source: {
        type: String,
        default: ''
    },
    audition: {
        type: Object,
        default: null
    },
    // Interstitial ad creative id (InterstitialAdModal.vue's creative.id) —
    // appended as psc5 attribution on every purchase URL below (see
    // appendGoldAttribution) so a GOLD conversion driven through this modal
    // stays linkable back to the specific ad/creative that opened it, even
    // when the click is short-circuted into the Plus Gold one-time flow
    // (AccountTrait::performGoldUpgrade forwards psc3/4/5 from the incoming
    // request when present — see that method's doc comment). Null for any
    // other JoinGoldModal caller (e.g. a future non-ad trigger).
    creativeId: {
        type: String,
        default: null
    },
    /**
     * Free-form props merged into every event this modal fires (placement,
     * product_line, trigger, membership_* ...). Mirrors the Blade twin's
     * third openJoinGoldModal() argument, so a new caller becomes reportable
     * by passing a source — nothing here is required.
     */
    context: {
        type: Object,
        default: () => ({})
    }
})

const emit = defineEmits(['close'])

const selectedOption = ref('Annual')
const joinOptionsData = usePage().props.joinOptions

/*
 * NEX-9344 — the redesign A/B test. Both come from HandleInertiaRequests, which reads the same
 * GoldModalExperiment the Blade twin does, so a member sees one design across every surface.
 * `copy` is null for control (never rendered, never shipped) and its prices are already resolved
 * server-side from the same joinOptions above — the disclaimer states billing terms, so it is not
 * re-derived here.
 */
const goldModalExperiment = computed(() => usePage().props.goldModal ?? { variant: 'default', copy: null })
const variant = computed(() => goldModalExperiment.value.variant ?? 'default')
const isRedesign = computed(() => variant.value === 'redesign' && !!goldModalExperiment.value.copy)
const experimentRunning = computed(() => goldModalExperiment.value.experimentRunning === true)

/**
 * NEX-9344 — mirrors an event into the in-house experiment pipeline
 * (nanalytics -> /event/track -> Redis HyperLogLog -> AdminCP goals).
 *
 * ADDITIVE ONLY. Every trackEvent() call below is untouched and still fires whether or not a
 * test is running: those LTIAnalytics events feed the existing ancms funnels and are not about
 * this test. These in-house events exist SOLELY for the A/B readout, so they are gated on a
 * live experiment — writing them otherwise puts junk into HyperLogLogs whose archived counts
 * can only ever be revised upwards.
 *
 * Guarded twice, like the Blade twin: experimentRunning says a test is live at all, and
 * nanalytics.trackExperiment() independently no-ops unless the exp-gold_modal_redesign and
 * exp-uid cookies both exist (the service deletes that cookie on every ineligible path).
 *
 * Event names must stay identical to join_gold_modal.blade.php — they are the contract with
 * the ExperimentGoal rows in AdminCP, so renaming one side orphans collected data.
 */
const GOLD_MODAL_EXPERIMENT_SLUG = 'gold_modal_redesign'

function trackExperimentEvent(event) {
    if (!experimentRunning.value) { return }
    if (!window.nanalytics || typeof window.nanalytics.trackExperiment !== 'function') { return }
    window.nanalytics.trackExperiment(event, GOLD_MODAL_EXPERIMENT_SLUG)
}

/**
 * Statsig twin of an LTIAnalytics event: same props, Statsig-style name. One event per action,
 * keyed on the member account id (the id Statsig assigned the arm under), sent only for members
 * Statsig enrolled (`statsigEnabled`). nanalytics is loaded by app2.blade.php.
 * Twin of the Blade partial's jgmTrackStatsig().
 */
const statsigEnabled = computed(() => goldModalExperiment.value.statsigEnabled === true)

function trackStatsigEvent(event, extra = {}) {
    if (!statsigEnabled.value) { return }
    if (!window.nanalytics || typeof window.nanalytics.logStatsigEvent !== 'function') { return }
    const userId = usePage().props.auth?.user?.id
    if (!userId) { return }
    const eventProps = goldModalEventProps(extra)
    window.nanalytics.logStatsigEvent(event, {
        userID: String(userId),
        value: eventProps.variant,
        metadata: eventProps,
    })
}
const redesignShared = computed(() => goldModalExperiment.value.copy ?? null)
const redesignCopy = computed(() => {
    const copy = redesignShared.value
    if (!copy) {
        return null
    }

    return lifetimeGoldOption.value ? copy.lifetime : copy.recurring
})
/** Bullets/badge for one plan, keyed by the plan name PricingHelper sets ('Annual'/'Monthly'). */
function redesignPlanCopy(name) {
    return redesignCopy.value?.plans?.[name] ?? { badge: null, bullets: [] }
}

/** Whether the comparison table is expanded. Collapsed on every open. */
const comparisonOpen = ref(false)

const joinOptions = computed(() => {
    const options = []
    if (joinOptionsData.button_yearlyGold) {
        options.push({
            name: joinOptionsData.button_yearlyGold.data.name,
            stars: joinOptionsData.button_yearlyGold.data.stars,
            stars_text: joinOptionsData.button_yearlyGold.data.stars_text,
            dailyDisplayPrice: `$${joinOptionsData.button_yearlyGold.data.formatted_daily_amount}`,
            fullDisplayPrice: `$${joinOptionsData.button_yearlyGold.data.formatted_initial_amount}`,
            period: joinOptionsData.button_yearlyGold.data.period,
            description: joinOptionsData.button_yearlyGold.data.included_content,
            includedContent: joinOptionsData.button_yearlyGold.data.included_content,
            url: joinOptionsData.button_yearlyGold.url,
            type: 'yearlyGold'
        })
    }
    if (joinOptionsData.button_monthlyGold) {
        options.push({
            name: joinOptionsData.button_monthlyGold.data.name,
            stars: joinOptionsData.button_monthlyGold.data.stars,
            stars_text: joinOptionsData.button_monthlyGold.data.stars_text,
            dailyDisplayPrice: `$${joinOptionsData.button_monthlyGold.data.formatted_daily_amount}`,
            fullDisplayPrice: `$${joinOptionsData.button_monthlyGold.data.formatted_initial_amount}`,
            period: joinOptionsData.button_monthlyGold.data.period,
            description: joinOptionsData.button_monthlyGold.data.included_content,
            includedContent: joinOptionsData.button_monthlyGold.data.included_content,
            url: joinOptionsData.button_monthlyGold.url,
            type: 'monthlyGold'
        })
    }

    return options;
})
const selectedOptionData = computed(() =>
    joinOptions.value.find((t) => t.name === selectedOption.value)
)

// Lifetime GOLD (PricingHelper::getLifetimeGoldOption()) is what this modal
// promotes now — null whenever it isn't a real option for this member
// (feature flag off, no active SKU, or PlusGoldHelper eligibility fails),
// in which case the template falls back to the recurring Annual/Monthly
// picker above rather than showing nothing.
const lifetimeGoldOption = computed(() => joinOptionsData.button_lifetimeGold ?? null)

// Split "199.95" into {dollars: '199', cents: '95'} for the big-integer/
// smaller-decimals price treatment matching /gold's .g-plan__amount +
// .g-plan__decimals (naGoldNew.blade.php).
const lifetimePriceParts = computed(() => {
    const amount = lifetimeGoldOption.value?.data?.formatted_initial_amount ?? '0.00'
    const [dollars, cents] = amount.split('.')
    return { dollars, cents: cents ?? '00' }
})
// button_yearlyGold/button_monthlyGold can each independently be null —
// PricingHelper::getFeedJoinOptions() sets a button to null rather than
// throwing when its own pricing lookup fails, same as the joinOptions
// computed above already accounts for via its `if (joinOptionsData.button_*)`
// guards. Optional-chained here too so a single failed lookup doesn't crash
// this modal entirely.
const yearlyTotalPrice = computed(() => `$${joinOptionsData.button_yearlyGold?.data.formatted_initial_amount ?? '—'}`)
const monthlyTotalPrice = computed(() => `$${joinOptionsData.button_monthlyGold?.data.formatted_initial_amount ?? '—'}`)

/**
 * Appends psc3/psc4/psc5 attribution onto a GOLD purchase URL so whichever
 * button the member clicks stays linkable back to the ad/creative that
 * opened this modal — mirrors PlusGoldHelper::attributionQuery()'s slot
 * semantics (psc3 = placement, psc4 = source page, psc5 = sub-element).
 * Lifetime's URL already carries psc3/psc4 baked in server-side (see
 * PricingHelper::getLifetimeGoldOption()) — this only ADDS psc5 there,
 * never overwriting what's already set. Annual/Monthly's URL has no psc
 * attribution at all until this runs; AccountTrait::performGoldUpgrade
 * forwards whatever it finds here into its own Plus Gold redirect when it
 * takes that branch (the common case whenever Plus Gold is enabled for
 * this member), which is what actually fires for most members clicking
 * any of the three buttons — see that method's doc comment.
 *
 * No-ops (returns url unchanged) when creativeId is null — i.e. whenever
 * this modal wasn't opened from an interstitial ad.
 */
/**
 * Whether the member is on their way to the biller, so the modal closing is
 * not reported as a dismissal. Mirrors the Blade twin's jgmJoining.
 */
const joining = ref(false)

/**
 * Props shared by every event this modal fires — see the `context` prop.
 * `extra` is per-event detail and wins over the caller's context.
 */
function goldModalEventProps(extra = {}) {
    return {
        source: props.source,
        creative_id: props.creativeId,
        // Always sent, 'default' included, so control is an explicit arm in reporting rather than
        // an absent field that could equally mean "not instrumented yet". Twin of the Blade
        // partial's jgmEventProps().
        variant: variant.value,
        // Needed for unique-member counts: the funnel pipes count
        // uniqExactIf(user_id). Read off Inertia's shared auth prop rather
        // than required from every caller, and still overridable via context.
        user_id: usePage().props.auth?.user?.id ?? null,
        // Which Gold offer this member gets and why — twin of the Blade jgmEventProps().
        ...(goldModalExperiment.value.offerContext ?? {}),
        ...props.context,
        ...extra,
    }
}

// The denominator for the whole modal funnel: an ad click that opened this
// and went nowhere used to look identical to one that reached the biller.
watch(() => props.isOpen, (isOpen) => {
    if (!isOpen) {
        return
    }

    joining.value = false
    comparisonOpen.value = false
    trackEvent('Join Gold Modal Shown', goldModalEventProps())
    trackStatsigEvent('join_gold_modal_shown')
    trackExperimentEvent('gold_modal_shown')
})

function selectOption(optionName, joinType) {
    selectedOption.value = optionName
    trackEvent('Join Gold Option Clicked', goldModalEventProps({ join_type: joinType ?? optionName }))
    trackExperimentEvent('gold_modal_option_clicked')
}

function dismiss() {
    if (!joining.value) {
        trackEvent('Join Gold Modal Dismissed', goldModalEventProps())
        trackExperimentEvent('gold_modal_dismissed')
    }

    emit('close')
}

/**
 * Appends psc3/psc4/psc5 attribution onto a GOLD purchase URL — the twin of
 * join_gold_modal.blade.php's jgmAppendGoldAttribution(), and kept identical
 * to it deliberately.
 *
 * Gated on `source`, not creativeId: a non-ad caller has no creative but
 * still needs psc4 stamped, and psc5 is the only piece that genuinely
 * depends on there being a creative.
 *
 * psc4 is always overwritten rather than set-if-absent. The Lifetime CTA's
 * URL arrives with a generic psc4=joinGoldModal already baked in
 * server-side (HandleInertiaRequests passes 'joinGoldModal' to
 * PricingHelper::getLifetimeGoldOption), so set-if-absent silently swallowed
 * the real source — an ad-driven Lifetime GOLD purchase from a Vue surface
 * reported as the modal itself and fell out of
 * interstitial_ad_gold_conversions.pipe, which matches psc4 on the
 * Interstitial_Ad_ prefix. The Blade twin was fixed for this on 2026-08-17;
 * this side was missed.
 *
 * psc3 stays the constant 'join_gold_modal' UI-mechanism marker (set only if
 * absent) — it describes the modal, not who opened it.
 */
function appendGoldAttribution(url) {
    if (!props.source) {
        return url
    }

    const parsed = new URL(url, window.location.origin)
    if (!parsed.searchParams.has('psc3')) {
        parsed.searchParams.set('psc3', 'join_gold_modal')
    }
    parsed.searchParams.set('psc4', props.source)
    if (props.creativeId) {
        parsed.searchParams.set('psc5', props.creativeId)
    }
    return parsed.toString()
}

function redirectToJoin(joinUrl, joinType, ctaPosition = 'primary') {
    console.log('++ JoinOptions: From ' + props.source + ' Redirecting to', joinUrl);

    // Suppresses the dismissal event — the modal goes away here as a
    // conversion step, not an abandonment.
    joining.value = true;
    localStorage.setItem('blue_join_options_source', props.source);
    localStorage.setItem('blue_join_options_join_type', joinType);
    localStorage.setItem('blue_join_options_url', joinUrl);

    if (props.audition) {

        LTIAnalytics.setCookie("gold_upgrade_source", props.source);
        LTIAnalytics.setCookie("gold_upgrade_video_id", props.audition.id ?? '');
        trackAuditionAnalyticsEvent('Biller Join Button Clicked', props.audition, {
            store_in_local_storage: true,
            local_storage_key: 'blue_join_options_video_props',
            append_data: goldModalEventProps({ join_type: joinType, cta_position: ctaPosition })
        });
    } else {
        // Carries creative_id and the opener's context now, so this step can
        // be joined to the ad that drove it. Additive for its existing
        // consumer (na_create_pitch_funnel.pipe in ancms).
        trackEvent('Biller Join Button Clicked', goldModalEventProps({ join_type: joinType, cta_position: ctaPosition }));
        trackExperimentEvent('gold_modal_biller_join_clicked')
    }

    window.location.href = joinUrl;
}

const handleJoin = (ctaPosition = 'primary') => {
    // Lifetime GOLD's URL already carries its own psc2/psc3/psc4/psc6
    // attribution (see PlusGoldHelper::purchaseHrefForPlacement()) — unlike
    // the recurring options below, it doesn't need a custom8 param appended.
    // appendGoldAttribution still runs, and its psc4 OVERWRITE is what matters
    // here: the psc4 baked into that URL is the generic 'joinGoldModal', not
    // whoever opened this modal.
    if (lifetimeGoldOption.value) {
        /*
         * Lifetime is a single button, so its click is both the option choice and the submit.
         * The Blade twin has always reported that as a 'Join Gold Option Clicked' + 'Biller Join
         * Button Clicked' pair; this side has only ever sent the second — a PRE-EXISTING twin
         * divergence, not something this ticket introduced.
         *
         * Deliberately fired for the redesign arm ONLY. Sending it for control too would be the
         * tidier fix, but it would change control's tracking mid-experiment and break comparison
         * with everything recorded before this ticket (AC #1: control behaves exactly as before).
         * Worth its own ticket to reconcile the two arms once a winner is picked.
         */
        if (isRedesign.value) {
            trackEvent('Join Gold Option Clicked', goldModalEventProps({ join_type: 'lifetimeGold', cta_position: ctaPosition }));
        }
        /*
         * OUTSIDE the isRedesign gate on purpose, unlike the LTIAnalytics line above.
         *
         * That gate exists to preserve control's historical Tinybird continuity. The in-house
         * experiment events have no history to preserve — they were added for this test — so
         * they must be SYMMETRIC across arms. Firing this one only for the redesign would mean
         * control never reports the option-click step on the Vue lifetime path, and any
         * shown -> option_clicked goal would show the redesign winning purely because its
         * opponent was not counted.
         */
        trackExperimentEvent('gold_modal_option_clicked')
        redirectToJoin(appendGoldAttribution(lifetimeGoldOption.value.url), 'lifetimeGold', ctaPosition);
        return;
    }

    const joinUrl = selectedOptionData.value.url +
        (selectedOptionData.value.url.includes('?') ?
            `&custom8=${props.source}` :
            `?custom8=${props.source}`)

    redirectToJoin(appendGoldAttribution(joinUrl), selectedOptionData.value.type, ctaPosition);
}

/**
 * Whether members open the comparison is a large part of what this variant is testing, so the
 * expand is a first-class event. Only the opening is reported — a collapse isn't a signal.
 */
function toggleComparison() {
    comparisonOpen.value = !comparisonOpen.value

    if (comparisonOpen.value) {
        trackEvent('Join Gold Comparison Expanded', goldModalEventProps())
        trackExperimentEvent('gold_modal_comparison_expanded')
    }
}

// Same 10 sites, logos, and display order as /joinPage's own "View all 11
// sites" gold modal (GoldController::buildGoldSitesForMa(), shared here as
// goldSites.bonus_sites_ma — see HandleInertiaRequests) — reused rather than
// a separately maintained list, so this grid can't drift from what members
// already see promoted there. `logo` is a fully-resolved asset() URL.
const bonusSites = computed(() => usePage().props.goldSites?.bonus_sites_ma ?? [])
</script>

<style scoped>
/* Thin gold scrollbar for the modal's own scroll container, matching the
   gold palette used throughout (crown badge / CTA gradient: #b8860b →
   #f0d078 → #c9a84c). Firefox via scrollbar-width/-color, WebKit browsers
   (Chrome/Safari/Edge) via the ::-webkit-scrollbar pseudo-elements. */
.jgm-scroll {
    scrollbar-width: thin;
    scrollbar-color: #c9a84c transparent;
}
.jgm-scroll::-webkit-scrollbar {
    width: 6px;
}
.jgm-scroll::-webkit-scrollbar-track {
    background: transparent;
}
.jgm-scroll::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #b8860b, #f0d078, #c9a84c);
    border-radius: 999px;
}
.jgm-scroll::-webkit-scrollbar-thumb:hover {
    background: #c9a84c;
}

/* Same dark hero treatment as /gold's .g-pricing section (naGoldNew.blade.php):
   a heavily grayscaled + darkened photo behind a soft gold radial glow, so
   the header + pricing/option area reads as the same moment regardless of
   which surface promoted GOLD. Layered as real elements (not ::before/::after)
   since Vue's scoped styles don't need the pseudo-element trick /gold uses. */
.jgm-hero {
    position: relative;
    overflow: hidden;
    isolation: isolate;
}
.jgm-hero-bg {
    position: absolute;
    inset: -10%;
    /* Absolute CDN URL, not a root-relative path: no NACMS host serves public/images, and
       CSS cannot call the cdnAsset() helper the templates use. See Helpers/assets.js. */
    background: url('https://sm.naughtycdn.com/images/gold/footer_tngf.webp') center center / cover no-repeat;
    filter: grayscale(100%);
    z-index: 0;
}
.jgm-hero-overlay {
    position: absolute;
    inset: 0;
    background: rgba(8, 8, 8, 0.82);
    z-index: 1;
}
.jgm-hero-glow {
    position: absolute;
    top: 10%;
    left: 50%;
    width: min(100%, 480px);
    height: 260px;
    transform: translateX(-50%);
    background: radial-gradient(circle, rgba(184, 134, 11, 0.28) 0%, rgba(184, 134, 11, 0.1) 35%, rgba(184, 134, 11, 0) 72%);
    filter: blur(50px);
    z-index: 2;
}
.jgm-hero-content {
    position: relative;
    z-index: 3;
}

/* Matches /gold's #g-pricing-heading + .g-pricing__heading-gold exactly:
   "Gold" gets the gold-gradient text-clip treatment, the rest stays white. */
.jgm-hero-heading {
    margin: 0;
    font-size: clamp(1.75rem, 6vw, 2.5rem);
    font-weight: 800;
    line-height: 0.95;
    letter-spacing: -0.02em;
    color: #fff;
}
.jgm-hero-heading-gold {
    background: linear-gradient(90deg, #b8860b 0%, #f0d078 48%, #c9a84c 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
}

.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.modal-enter-active .max-w-md,
.modal-leave-active .max-w-md {
    transition: transform 0.3s ease;
}

.modal-enter-from .max-w-md,
.modal-leave-to .max-w-md {
    transform: scale(0.95);
}

/* ====================================================================
   NEX-9344 redesign variant (jgmx-). Twin of the Blade partial's jgmx- block —
   keep the two in sync; see the `blade-vue-twins` skill.

   `scoped` already prevents these leaking, so unlike the Blade side they need
   no #interstitialAdCtaModal prefix. But scoped ALSO puts a variant class at
   equal specificity to its base, so anything overriding a base rule must stay
   AFTER it in source order — the trap .ia-cta--fyf hit. These classes are all
   new, so nothing here competes; keep it that way.
   ==================================================================== */
.jgmx-modal {
    position: relative;
    width: 100%;
    max-width: 456px;
    max-height: 94vh;
    overflow-y: auto;
    padding: 34px 30px 22px;
    border: 1px solid #2A251F;
    border-radius: 20px;
    color: #F0EDE6;
    text-align: center;
    background:
        radial-gradient(90% 60% at 50% -8%, rgba(224, 180, 70, .16), transparent 60%),
        linear-gradient(180deg, #141109, #0d0b08);
    box-shadow:
        0 40px 90px -20px rgba(0, 0, 0, .8),
        0 0 0 1px rgba(248, 213, 127, .05),
        inset 0 1px 0 rgba(248, 213, 127, .08);
}
/* Gold hairline across the top edge. */
.jgmx-modal::before {
    content: "";
    position: absolute;
    top: 0;
    left: 26px;
    right: 26px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(248, 213, 127, .55), transparent);
}
.jgmx-close {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 10;
    width: 32px;
    height: 32px;
    padding: 6px;
    border: none;
    border-radius: 8px;
    background: rgba(255, 255, 255, .05);
    color: #B4AEA2;
    cursor: pointer;
}
.jgmx-close:hover { background: rgba(255, 255, 255, .1); color: #F0EDE6; }
.jgmx-close svg { width: 100%; height: 100%; }

.jgmx-emblem { position: relative; display: block; width: 68px; height: 68px; margin: 2px auto 0; }
.jgmx-emblem-glow {
    position: absolute; inset: -28px; border-radius: 50%;
    background: radial-gradient(circle, rgba(230, 183, 72, .24), transparent 60%);
}
.jgmx-emblem-ring {
    position: absolute; inset: 0; border-radius: 50%;
    border: 1px solid rgba(248, 213, 127, .32);
    background: radial-gradient(circle at 50% 32%, #1b160f, #0d0b07);
    display: flex; align-items: center; justify-content: center;
    box-shadow: inset 0 0 15px rgba(0, 0, 0, .55);
}
.jgmx-emblem-ring svg { width: 32px; height: 32px; filter: drop-shadow(0 2px 3px rgba(0, 0, 0, .5)); }

.jgmx-eyebrow {
    margin: 18px 0 0; font-size: 12px; font-weight: 600;
    letter-spacing: .3em; text-transform: uppercase; color: #B4AEA2;
}
.jgmx-title {
    margin: 14px 0 0; font-size: 30px; font-weight: 700; line-height: 1.16;
    letter-spacing: -.015em; color: #F0EDE6;
}
.jgmx-title-gold {
    background: linear-gradient(158deg, #F8D57F 0%, #E3B441 46%, #A9741C 100%);
    -webkit-background-clip: text; background-clip: text; color: transparent;
}
.jgmx-sub { margin: 14px auto 0; max-width: 342px; font-size: 15px; line-height: 1.55; color: #D9D3C8; }
.jgmx-sub :deep(b) { color: #F6D165; font-weight: 600; }

.jgmx-pricebox {
    position: relative; margin: 24px 0 0; padding: 20px 16px 18px;
    border: 1px solid rgba(248, 213, 127, .13); border-radius: 16px;
    background: rgba(224, 180, 70, .035);
}
.jgmx-onetime {
    display: inline-flex; align-items: center; gap: 11px; margin: 0 0 10px;
    font-size: 12px; font-weight: 700; letter-spacing: .24em;
    text-transform: uppercase; color: #F6D165;
}
.jgmx-onetime::before, .jgmx-onetime::after {
    content: ""; width: 22px; height: 1px; background: rgba(248, 213, 127, .45);
}
.jgmx-price {
    font-size: 52px; font-weight: 700; line-height: 1; letter-spacing: -.02em;
    background: linear-gradient(158deg, #F8D57F 0%, #E3B441 46%, #A9741C 100%);
    -webkit-background-clip: text; background-clip: text; color: transparent;
}
.jgmx-price sup {
    font-size: .4em; font-weight: 600; vertical-align: top; line-height: 1;
    margin-left: 2px; position: relative; top: .14em;
}
.jgmx-price-note { margin: 10px 0 0; font-size: 13px; font-weight: 500; color: #D9D3C8; }
.jgmx-price-note :deep(b) { color: #F6D165; font-weight: 700; }

.jgmx-planbox {
    position: relative; margin: 24px 0 0; padding: 18px 14px 16px;
    border: 1px solid rgba(248, 213, 127, .13); border-radius: 16px;
    background: rgba(224, 180, 70, .035);
}
.jgmx-planbox-label { text-align: center; margin-bottom: 14px; }
.jgmx-plans { display: grid; gap: 10px; }
.jgmx-plan {
    position: relative; display: block; width: 100%; text-align: left;
    padding: 15px 15px 13px; border-radius: 13px; cursor: pointer;
    background: rgba(255, 255, 255, .018); border: 1px solid #2A251F;
    transition: border-color .18s ease, background .18s ease, box-shadow .18s ease;
}
.jgmx-plan:hover { border-color: rgba(248, 213, 127, .3); background: rgba(224, 180, 70, .045); }
.jgmx-plan--active {
    border-color: rgba(248, 213, 127, .55);
    background: radial-gradient(120% 100% at 50% 0%, rgba(224, 180, 70, .14), rgba(224, 180, 70, .05) 70%);
    box-shadow: 0 0 0 1px rgba(248, 213, 127, .14), 0 10px 30px -18px rgba(224, 180, 70, .55);
}
.jgmx-plan-badge {
    position: absolute; top: -9px; left: 15px; padding: 3px 10px; border-radius: 999px;
    background: linear-gradient(158deg, #F8D57F 0%, #E3B441 46%, #A9741C 100%);
    color: #1a1305; font-size: 9.5px; font-weight: 800; letter-spacing: .09em;
    text-transform: uppercase; white-space: nowrap;
    box-shadow: 0 4px 12px -4px rgba(224, 180, 70, .6);
}
.jgmx-plan-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.jgmx-plan-amt { display: flex; align-items: baseline; gap: 3px; }
.jgmx-plan-day { font-size: 32px; font-weight: 700; line-height: 1; letter-spacing: -.02em; color: #D9D3C8; }
.jgmx-plan--active .jgmx-plan-day {
    background: linear-gradient(158deg, #F8D57F 0%, #E3B441 46%, #A9741C 100%);
    -webkit-background-clip: text; background-clip: text; color: transparent;
}
.jgmx-plan-per { font-size: 13px; font-weight: 500; color: #B4AEA2; }
.jgmx-plan-name {
    display: block; margin-top: 7px; font-size: 10.5px; font-weight: 700;
    letter-spacing: .2em; text-transform: uppercase; color: #B4AEA2;
}
.jgmx-plan--active .jgmx-plan-name { color: #F6D165; }
/* Explicit radio affordance — control signals "unselected" with 70% opacity,
   which on a dark panel reads as disabled rather than not-chosen. */
.jgmx-plan-tick {
    flex: 0 0 auto; width: 19px; height: 19px; margin-top: 3px; border-radius: 50%;
    border: 1px solid #2A251F; display: flex; align-items: center; justify-content: center;
}
.jgmx-plan--active .jgmx-plan-tick {
    border-color: transparent;
    background: linear-gradient(158deg, #F8D57F 0%, #E3B441 46%, #A9741C 100%);
}
.jgmx-plan-tick svg { width: 10px; height: 10px; opacity: 0; }
.jgmx-plan--active .jgmx-plan-tick svg { opacity: 1; }
.jgmx-plan-inc { display: grid; gap: 6px; margin-top: 11px; }
.jgmx-plan-inc-item { display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: #D9D3C8; }
.jgmx-plan-ck {
    flex: 0 0 auto; width: 14px; height: 14px; border-radius: 50%;
    background: rgba(248, 213, 127, .16);
    display: flex; align-items: center; justify-content: center;
}
.jgmx-plan-ck svg { width: 8px; height: 8px; }
.jgmx-plan-ck svg path { stroke: #F6D165; }
.jgmx-plan--active .jgmx-plan-ck { background: linear-gradient(158deg, #F8D57F 0%, #E3B441 46%, #A9741C 100%); }
.jgmx-plan--active .jgmx-plan-ck svg path { stroke: #1a1305; }
.jgmx-billing { margin: 13px 0 0; font-size: 12px; line-height: 1.5; color: #B4AEA2; }
.jgmx-billing-sep { color: #9A6A1B; margin: 0 5px; }

.jgmx-cta {
    display: flex; align-items: center; justify-content: center; gap: 9px;
    width: 100%; margin: 16px 0 0; padding: 17px; border: none; border-radius: 12px;
    background: #E8C04C; color: #1a1305;
    font-size: 17px; font-weight: 700; letter-spacing: .01em; cursor: pointer;
    box-shadow: 0 8px 24px -10px rgba(224, 180, 70, .5);
    transition: transform .16s ease, box-shadow .16s ease, filter .16s ease;
}
.jgmx-cta:hover {
    filter: brightness(1.06); transform: translateY(-1px);
    box-shadow: 0 14px 32px -8px rgba(224, 180, 70, .62);
}
.jgmx-cta:active { transform: translateY(0); }
.jgmx-cta svg { width: 16px; height: 16px; }
.jgmx-cta--secondary { margin-top: 20px; }
.jgmx-reassure { margin: 12px 0 0; font-size: 13px; font-weight: 500; color: #D9D3C8; }
.jgmx-reassure-sep { color: #9A6A1B; margin: 0 7px; }

.jgmx-benefits {
    display: grid; gap: 13px; margin: 22px 0 0; padding: 20px 2px;
    border-top: 1px solid rgba(213, 165, 56, .18);
    border-bottom: 1px solid rgba(213, 165, 56, .18);
}
.jgmx-benefit { display: flex; align-items: center; gap: 12px; font-size: 15px; color: #F0EDE6; text-align: left; }
.jgmx-benefit b { font-weight: 600; }
.jgmx-benefit-ck {
    flex: 0 0 auto; width: 21px; height: 21px; border-radius: 50%;
    background: linear-gradient(158deg, #F8D57F 0%, #E3B441 46%, #A9741C 100%);
    display: flex; align-items: center; justify-content: center;
}
.jgmx-benefit-ck svg { width: 11px; height: 11px; }
.jgmx-benefit-det { font-size: 13px; font-weight: 400; color: #B4AEA2; }

.jgmx-toggle {
    display: flex; align-items: center; justify-content: center; gap: 8px;
    width: 100%; margin: 16px 0 0; padding: 6px;
    background: none; border: none; cursor: pointer;
    font-size: 13.5px; font-weight: 600; color: #F6D165;
}
.jgmx-toggle:hover { color: #fbe08c; }
.jgmx-chev { font-size: 12px; transition: transform .3s ease; }
.jgmx-chev--open { transform: rotate(180deg); }
/* max-height rather than display:none so the expand is animatable; 1200px clears
   the tallest the table gets at 320px wide. */
.jgmx-compare { max-height: 0; overflow: hidden; transition: max-height .4s ease; }
.jgmx-compare--open { max-height: 1200px; }
.jgmx-compare-inner { padding: 16px 0 4px; }
.jgmx-cmp { width: 100%; border-collapse: collapse; }
.jgmx-cmp th, .jgmx-cmp td {
    padding: 11px 6px; text-align: left; vertical-align: middle; border-bottom: 1px solid #2A251F;
}
.jgmx-cmp thead th { border-bottom: 1px solid rgba(213, 165, 56, .18); }
.jgmx-cmp-trial { font-size: 12px; font-weight: 500; color: #B4AEA2; }
.jgmx-cmp-na, .jgmx-cmp-gold {
    width: 74px; text-align: center; font-size: 12px; font-weight: 700; letter-spacing: .02em;
}
.jgmx-cmp-na { color: #D9D3C8; }
.jgmx-cmp-gold { color: #F6D165; }
.jgmx-cmp-feat { font-size: 13.5px; line-height: 1.35; color: #F0EDE6; }
.jgmx-cmp-navalue, .jgmx-cmp-goldvalue { text-align: center; font-size: 14px; font-weight: 700; }
.jgmx-cmp-navalue { color: #D9D3C8; }
.jgmx-cmp-goldvalue { color: #F6D165; }
.jgmx-cmp-yes { color: #63D08A; font-size: 15px; }
.jgmx-cmp-no { color: #B4AEA2; font-size: 15px; }
.jgmx-sitelist { margin: 16px 0 0; padding: 0; columns: 2; column-gap: 16px; }
.jgmx-sitelist li {
    list-style: none; padding: 4px 0; break-inside: avoid; text-align: left;
    font-size: 12px; font-weight: 600; letter-spacing: .03em;
    text-transform: uppercase; color: #D9D3C8;
}
.jgmx-cmp-note { margin: 12px 0 0; font-size: 12px; font-style: italic; color: #B4AEA2; text-align: left; }

.jgmx-disclaimer {
    margin: 20px 0 0; padding-top: 16px; font-size: 10px; line-height: 1.5; color: #B4AEA2;
    border-top: 1px solid rgba(213, 165, 56, .18);
}
.jgmx-later {
    display: block; margin: 12px auto 2px; padding: 6px;
    background: none; border: none; cursor: pointer; font-size: 13px; color: #B4AEA2;
}
.jgmx-later:hover { color: #F0EDE6; }

@media (max-width: 480px) {
    .jgmx-modal { padding: 30px 20px 20px; border-radius: 18px; }
    .jgmx-title { font-size: 27px; }
    .jgmx-price { font-size: 46px; }
}
@media (prefers-reduced-motion: reduce) {
    .jgmx-cta, .jgmx-compare, .jgmx-chev, .jgmx-plan { transition: none; }
}

/* Override text-sm to be 12px instead of default 14px */
.text-xs {
    font-size: 12px;
}
</style>
